import type { z } from 'zod';

import type { InstallationListItemDto, InstallationListParams } from './installations.types';

import { createAdminListSearchSchema } from '@/router/listSearch';
import {
  buildFilterParams,
  collectSearchTerms,
  type FilterParamMap,
} from '@/utils/listQueryParams';

/**
 * The admin Installations list (HS-1631): URL state as every factory list keeps it — `page`, `q`,
 * `filters` — with no sort, because HS-1620 fixes the order (newest first) and the design draws no
 * sortable header (HS-980 decision U16).
 */
export const installationsSearchSchema = createAdminListSearchSchema();

export type InstallationsSearch = z.infer<typeof installationsSearchSchema>;

/** The two filter-builder fields `GET /api/Installation/list` accepts (HS-1620). */
export const INSTALLATIONS_STATUS_FIELD = 'status';
export const INSTALLATIONS_INSTITUTION_FIELD = 'institutionId';

export const INSTALLATIONS_FILTER_PARAMS: FilterParamMap<InstallationListParams> = {
  [INSTALLATIONS_STATUS_FIELD]: { kind: 'multi', param: 'status' },
  [INSTALLATIONS_INSTITUTION_FIELD]: { kind: 'multi', param: 'institutionId' },
};

/**
 * Turn the URL into the list's params; shared by the screen's query, its filter panel and the
 * metrics row, so all three read the same cache entry.
 */
export function installationsQueryParams(search: InstallationsSearch): InstallationListParams {
  const conditions = search.filters ?? [];
  const searchTerms = collectSearchTerms(conditions, search.q ?? '', INSTALLATIONS_FILTER_PARAMS);
  return {
    page: search.page,
    pageSize: search.pageSize,
    ...(searchTerms ? { search: searchTerms } : {}),
    ...buildFilterParams(conditions, INSTALLATIONS_FILTER_PARAMS),
  };
}

/** The text each column holds for a row — what search, the filter builder and the cards read. */
export const installationsColumnText = {
  model: (row: InstallationListItemDto) => row.device.model.name,
  hood: (row: InstallationListItemDto) => row.hood.name,
  institution: (row: InstallationListItemDto) => row.institution.name,
  laboratory: (row: InstallationListItemDto) => row.lab.name,
  status: (row: InstallationListItemDto) => row.status,
  installed: (row: InstallationListItemDto) => row.completedAt ?? '',
  installer: (row: InstallationListItemDto) => row.installer.fullName,
} as const;

export type InstallationsColumnId = keyof typeof installationsColumnText;
